/**
 * How far a room's content reaches beyond its primary rectangle: alcove
 * depths plus out-swinging door clearance.
 */
import { doorSwingShape } from '../doorSwingShape';
import type { PlanBounds } from '../geometry';
import { type EdgeName, edgeSegments, type FloorPlanEdgeSegment, type FloorPlanRoom } from '../schemas';

/** How far one edge segment reaches outward: an alcove its depth, a door its longest out-swinging leaf. */
const segmentReach = (segment: FloorPlanEdgeSegment): number => {
  if (segment.edgeSegmentType === 'alcove') return segment.depth ?? 0;
  if (segment.edgeSegmentType !== 'door') return 0;
  return Math.max(0, ...doorSwingShape(segment.swing).leaves.filter(leaf => leaf.intoSign < 0).map(leaf => leaf.widthShare * segment.width));
};

/** How far an edge's content reaches outward: alcove depths plus out-swinging door clearance. */
export function outwardReach(shape: FloorPlanRoom, edge: EdgeName): number {
  return edgeSegments(shape, edge).reduce((reach, segment) => Math.max(reach, segmentReach(segment)), 0);
}

/** The bounding box of the room plus everything reaching outward from it. */
export function geometryBounds(shape: FloorPlanRoom): PlanBounds {
  return {
    minX: -outwardReach(shape, 'leftEdge'),
    maxX: shape.width + outwardReach(shape, 'rightEdge'),
    minY: -outwardReach(shape, 'backEdge'),
    maxY: shape.length + outwardReach(shape, 'frontEdge'),
  };
}
